import {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from "react";

import { cn, flattenStyles, platformProps } from "../../lib";
import { getAriaState } from "../../lib/aria-state";
import { isPendingAware, markPendingAware, PENDING_STRIPPED_PROPS } from "../../lib/pending";
import { SlotPressable } from "../slot";
import { startedOnInnerControl } from "./lib";
import type { PressableProps, PressableState } from "./types";

/**
 * Handlers a parent can inject at runtime (e.g. a Radix `asChild` trigger
 * wrapping this Pressable). They are not part of the public props, but they
 * must run alongside Pressable's own handlers rather than replace them.
 */
type InjectedHandlers = {
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  onKeyUp?: (event: KeyboardEvent<HTMLElement>) => void;
  onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
  onPointerCancel?: (event: PointerEvent<HTMLElement>) => void;
};

/** What a pending Pressable reports to assistive tech. */
const PENDING_ARIA = { "aria-disabled": true, "aria-busy": true } as const;

/**
 * Whether the browser already turns this key into a click on this element:
 * Enter on buttons and links, Space on buttons only (Space on a link scrolls).
 */
function activatesNatively(el: HTMLElement, key: string): boolean {
  const isButtonLike =
    el instanceof HTMLButtonElement ||
    (el instanceof HTMLInputElement && ["button", "submit", "reset", "image"].includes(el.type));
  if (isButtonLike) return true;
  return key === "Enter" && el instanceof HTMLAnchorElement && el.hasAttribute("href");
}

/**
 * Pressable - Web implementation
 *
 * Cross-platform interactive wrapper that handles touch/click, hover, and keyboard interactions.
 * Supports the asChild pattern via Slot for composing with any element.
 *
 * Every activation — mouse, touch, Enter, Space — arrives as one `click`, so the
 * disabled and long-press guards in `handleClick` apply to all of them.
 */
export const Pressable = forwardRef<HTMLButtonElement, PressableProps>(
  (
    {
      onClick: onClickProp,
      onPointerDown: onPointerDownProp,
      onPointerUp: onPointerUpProp,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      onLongClick,
      onHoverIn,
      onHoverOut,
      disabled = false,
      pending = false,
      asChild = false,
      children,
      className,
      style,
      accessibilityLabel,
      accessibilityHint,
      accessibilityRole = "button",
      accessibilityState,
      delayLongClick = 500,
      hitSlop: _hitSlop, // Native-only, ignored on web
      testID,
      id,
      ...rest
    },
    ref,
  ) => {
    const {
      onKeyDown: onKeyDownProp,
      onKeyUp: onKeyUpProp,
      onMouseEnter: onMouseEnterProp,
      onMouseLeave: onMouseLeaveProp,
      onPointerCancel: onPointerCancelProp,
      role: _role, // not a public prop; the rendered role comes from accessibilityRole
      ...props
    } = rest as typeof rest & InjectedHandlers & { role?: unknown };

    // === State ===
    const [pressed, setPressed] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);

    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressTriggered = useRef(false);
    const blockedRef = useRef(false);
    // Space activates on release, so remember the press started on this element
    const spacePressed = useRef(false);

    const state: PressableState = { pressed, hovered, focused };
    // Pending blocks every activation, like disabled, but the element stays
    // enabled: no `disabled` styles, and it keeps focus and its tab stop
    const blocked = disabled || pending;

    const clearLongPressTimer = useCallback(() => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }, []);

    const cancelPress = useCallback(() => {
      setPressed(false);
      spacePressed.current = false;
      clearLongPressTimer();
    }, [clearLongPressTimer]);

    // === Event Handlers ===

    const handleClick = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        // A click on a control nested inside belongs to that control
        if (startedOnInnerControl(e.target, e.currentTarget)) return;
        // With asChild, the child's own onClick runs first; respect it cancelling
        if (asChild && e.defaultPrevented) return;
        if (blocked) {
          // A disabled or pending asChild link must not navigate
          if (asChild) e.preventDefault();
          return;
        }
        if (longPressTriggered.current) {
          longPressTriggered.current = false;
          return;
        }
        onClickProp?.(e);
      },
      [asChild, blocked, onClickProp],
    );

    const handlePointerDown = useCallback(
      (e: PointerEvent<HTMLElement>) => {
        if (blocked || startedOnInnerControl(e.target, e.currentTarget)) return;
        longPressTriggered.current = false;
        setPressed(true);
        onPointerDownProp?.(e);

        if (onLongClick) {
          // One long press at a time: a second pointer replaces the first's timer
          clearLongPressTimer();
          longPressTimer.current = setTimeout(() => {
            // Disabled or pending since it was scheduled, e.g. by onPointerDown itself
            if (blockedRef.current) return;
            longPressTriggered.current = true;
            onLongClick();
          }, delayLongClick);
        }
      },
      [blocked, onPointerDownProp, onLongClick, delayLongClick, clearLongPressTimer],
    );

    const handlePointerUp = useCallback(
      (e: PointerEvent<HTMLElement>) => {
        setPressed(false);
        onPointerUpProp?.(e);
        clearLongPressTimer();
      },
      [onPointerUpProp, clearLongPressTimer],
    );

    const handlePointerCancel = useCallback(
      (e: PointerEvent<HTMLElement>) => {
        onPointerCancelProp?.(e);
        cancelPress();
      },
      [onPointerCancelProp, cancelPress],
    );

    const handleMouseEnter = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        onMouseEnterProp?.(e);
        setHovered(true);
        onHoverIn?.();
      },
      [onMouseEnterProp, onHoverIn],
    );

    const handleMouseLeave = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        onMouseLeaveProp?.(e);
        setHovered(false);
        cancelPress();
        onHoverOut?.();
      },
      [onMouseLeaveProp, onHoverOut, cancelPress],
    );

    const handleFocus = useCallback(
      (e: FocusEvent<HTMLElement>) => {
        onFocusProp?.(e);
        // Only show focus for keyboard navigation
        if (e.target.matches(":focus-visible")) {
          setFocused(true);
        }
      },
      [onFocusProp],
    );

    const handleBlur = useCallback(
      (e: FocusEvent<HTMLElement>) => {
        onBlurProp?.(e);
        setFocused(false);
        cancelPress();
      },
      [onBlurProp, cancelPress],
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLElement>) => {
        // Disabled or pending takes no keyboard activation, including a
        // wrapping trigger's (a Radix DropdownMenu opens on keydown). A
        // disabled button can't have focus, but a disabled asChild child
        // (tabIndex -1) can be focused programmatically.
        if (disabled || pending) return;
        onKeyDownProp?.(e);
        // Keys pressed in a nested control belong to that control
        if (e.defaultPrevented || e.target !== e.currentTarget) return;

        const isEnter = e.key === "Enter";
        const isSpace = e.key === " " && accessibilityRole !== "link";
        if (!isEnter && !isSpace) return;
        // One activation per physical press: stop a held key from repeating the click
        if (e.repeat) {
          e.preventDefault();
          return;
        }

        longPressTriggered.current = false;
        setPressed(true);
        onPointerDownProp?.(e);

        if (activatesNatively(e.currentTarget, e.key)) return; // the browser fires the click
        e.preventDefault(); // no page scroll on Space
        if (isEnter) {
          e.currentTarget.click();
        } else {
          spacePressed.current = true;
        }
      },
      [pending, onKeyDownProp, disabled, accessibilityRole, onPointerDownProp],
    );

    const handleKeyUp = useCallback(
      (e: KeyboardEvent<HTMLElement>) => {
        onKeyUpProp?.(e);
        if (e.key !== "Enter" && e.key !== " ") return;
        if (e.target !== e.currentTarget) return;

        setPressed(false);
        const releasesSpacePress = e.key === " " && spacePressed.current;
        spacePressed.current = false;
        if (disabled) return;
        // A release, so it still arrives while pending
        onPointerUpProp?.(e);
        if (pending) return;

        // A wrapper cancelling the keyup cancels the Space activation
        if (releasesSpacePress && !e.defaultPrevented) {
          e.preventDefault();
          e.currentTarget.click();
        }
      },
      [onKeyUpProp, disabled, pending, onPointerUpProp],
    );

    // Disabling or pending mid-press drops the press and any long press under
    // way. A layout effect, so it runs before that long press's timer can fire;
    // the timer also checks the committed state, for one scheduled after it ran.
    useLayoutEffect(() => {
      blockedRef.current = blocked;
      if (blocked) cancelPress();
    }, [blocked, cancelPress]);

    // Cleanup long press timer on unmount
    useEffect(() => clearLongPressTimer, [clearLongPressTimer]);

    // === Resolve Props ===
    const resolvedClassName = typeof className === "function" ? className(state) : className;
    const rawStyle = typeof style === "function" ? style(state) : style;
    const resolvedStyle = rawStyle ? flattenStyles([rawStyle]) : undefined;
    const resolvedChildren = typeof children === "function" ? children(state) : children;

    // Common props for both button and asChild
    const interactiveProps = {
      onClick: handleClick,
      onPointerDown: handlePointerDown,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerCancel,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      // Only when set: with asChild an undefined value reaches the child's props
      // and can override the label or hint a child component renders itself
      ...(accessibilityLabel !== undefined && { "aria-label": accessibilityLabel }),
      ...(accessibilityHint !== undefined && { "aria-description": accessibilityHint }),
      ...platformProps({ testID, id, slot: "pressable" }),
    };

    // === Render ===

    if (asChild) {
      if (!isValidElement(resolvedChildren)) {
        console.warn("Pressable with asChild requires a valid React element as child");
        return null;
      }

      // The child's own role wins in the Slot merge, including an explicit
      // role={undefined}, so map state against the role that actually renders
      const childProps = resolvedChildren.props as { role?: string | null };
      const role = "role" in childProps ? childProps.role : accessibilityRole;

      // While disabled or pending, the Slot would run the child's own handlers
      // before this Pressable's guard. So a child that understands `pending`
      // becomes disabled (either side disables both, as on native) and/or
      // pending itself; any other loses the activation handlers it was given.
      // The pending ARIA goes on the child, whose own values win the Slot merge.
      const child = blocked
        ? cloneElement(resolvedChildren, {
            ...(isPendingAware(resolvedChildren.type)
              ? { ...(disabled && { disabled: true }), ...(pending && { pending: true }) }
              : PENDING_STRIPPED_PROPS),
            ...(pending && PENDING_ARIA),
          })
        : resolvedChildren;

      return (
        <SlotPressable
          ref={ref as React.Ref<HTMLElement>}
          role={role ?? undefined}
          tabIndex={disabled ? -1 : 0}
          className={resolvedClassName}
          style={resolvedStyle}
          aria-disabled={disabled}
          {...interactiveProps}
          {...getAriaState(role, accessibilityState)}
          {...props}
        >
          {child}
        </SlotPressable>
      );
    }

    // Default: render as button
    return (
      <button
        ref={ref}
        type="button"
        // `button` is implicit; the native disabled attribute conveys disabled
        role={accessibilityRole === "button" ? undefined : accessibilityRole}
        disabled={disabled}
        className={cn(
          "appearance-none border-0 bg-transparent p-0 text-left",
          // Cursor
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          resolvedClassName,
        )}
        style={resolvedStyle}
        {...interactiveProps}
        {...getAriaState(accessibilityRole, accessibilityState)}
        {...props}
        // Last, so an injected aria-disabled={false} can't win over pending
        {...(pending && PENDING_ARIA)}
      >
        {resolvedChildren}
      </button>
    );
  },
);

Pressable.displayName = "Pressable";
markPendingAware(Pressable);
